import {
  CheckCircleTwoTone,
  ControlTwoTone,
  CopyTwoTone,
  DeleteOutlined,
  EyeInvisibleTwoTone,
  EyeTwoTone,
  IdcardTwoTone,
  MoneyCollectTwoTone,
  PauseCircleTwoTone,
  ScheduleTwoTone,
  TagsTwoTone,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { SchedulerProvider, SchedulersList } from '@teamco/ischeduler-antd';
import {
  Button,
  Col,
  Collapse,
  type CollapseProps,
  Flex,
  Form,
  type FormProps,
  Modal,
  Row,
  Switch,
} from 'antd';
import { t } from 'i18next';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { deleteWarning } from '@client/components/actions/modal.delete';
import { CloseButton, SaveButton } from '@client/components/buttons';
import { EditPageTitle } from '@client/components/EditPageTitle';
import { formMainInfo } from '@client/components/formMainInfo';
import { PrivacyTag } from '@client/components/PrivacyTag';
import { QuestionTooltip } from '@client/components/QuestionTooltip';
import { StatusTag } from '@client/components/StatusTag';
import { TagsComponent } from '@client/components/tags/TagsComponent';
import { Toolbar } from '@client/components/Toolbar';
import { NotificationContext } from '@client/context/NotificationContext';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useFeature } from '@client/hooks/useFeature';
import { useFormProps } from '@client/hooks/useFormProps';
import { useOnFinish } from '@client/hooks/useOnFinish';
import { usePrompt } from '@client/hooks/usePrompt';
import {
  useAssignedSchedulers,
  useSchedulers,
} from '@client/hooks/useSchedulers';
import { PageLayout } from '@client/layouts/page.layout';
import { FeatureDiscount } from '@client/pages/features/feature/feature.discount';
import { FeatureInfo } from '@client/pages/features/feature/feature.info';
import { FeatureTrial } from '@client/pages/features/feature/feature.trial';
import { FeatureUtility } from '@client/pages/features/feature/feature.utility';
import { subscriptionKeys } from '@client/queries/useSubscriptionQueries';
import {
  areSameScheduler,
  createSchedulers,
  schedulerDatesToDayjs,
  syncSchedulers,
} from '@client/services/scheduler.service';
import {
  getSubscriptionsByFeature,
  syncFeatureSubscriptions,
} from '@client/services/subscription.service';
import { useAssigned } from '@client/store/assigned.store';
import { useDirty } from '@client/store/dirty.store';
import { useLoading } from '@client/store/loading.store';
import { CNsTags } from '@client/types/form.type';
import { ERoutePaths } from '@client/types/router.type';
import { isEditMode } from '@client/utils/form.util';
import { flexLayout } from '@client/utils/layout.util';
import { successSaveMsg } from '@client/utils/message.util';
import {
  isDisabledScheduler,
  onCreateScheduler as handleCreate,
  onUpdateScheduler as handleUpdate,
  onDeleteScheduler as handleDelete,
} from '@client/utils/scheduler.util';
import {
  DEFAULT_FEATURE,
  EFeatureType,
  CNsPrice,
  ECurrency,
  type IFeature,
  CScheduler,
  ESchedulerPrefix,
  EDurationTypes,
  EStatus,
  CSchedulerEntityPaths,
  type IMetadata,
  type IScheduler,
  EPrivacyStatus,
  utilityCounter,
  type ISubscription,
} from '@common/types';

import styles from '../features.module.less';

import { FeatureSubscriptions } from './feature.subscriptions';
import { SubscriptionsDrawerButton } from './panels/SubscriptionsDrawerButton';

import type { ItemType } from 'antd/es/menu/interface';

export const FeatureEditPage: React.FC = () => {
  const [formRef] = Form.useForm();
  const { ability } = useAuth();
  const { isDirty, setDirty } = useDirty();
  const { setLoading } = useLoading();
  const queryClient = useQueryClient();
  const { modalApi, messageApi } = useContext(NotificationContext);

  const [isTrialed, setIsTrialed] = useState(false);
  const [activeKeys, setActiveKeys] = useState<string[]>(['info']);

  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isEdit = isEditMode(params.featureId);

  const { assigned: assignedSubscriptions, setAssigned } =
    useAssigned<ISubscription>();

  const handleChange = (keys: string | string[]) => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys]);
  };

  const { assignedSchedulers, setAssignedSchedulers } = useAssignedSchedulers({
    [ESchedulerPrefix.SALE]: [],
    [ESchedulerPrefix.DISCOUNT]: [],
    [ESchedulerPrefix.TRIAL_DISCOUNT]: [],
  });

  const {
    loading,
    feature,
    onActivate,
    onCreate,
    onDelete,
    onUpdate,
    onPublic,
    refetch,
  } = useFeature();

  const fetchSubscriptions = useCallback(() => {
    getSubscriptionsByFeature(params.featureId as string).then((subs) => {
      setAssigned(subs || []);
    });
  }, [params.featureId, setAssigned]);

  useEffect(() => {
    if (isEdit && feature) {
      fetchSubscriptions();
    } else if (!isEdit) {
      setAssigned([]);
    }
  }, [isEdit, feature, setAssigned, fetchSubscriptions]);

  const slugLocked = isEdit && feature?.status === EStatus.ACTIVE;

  const {
    loading: saleLoading,
    refetch: saleOnRefresh,
    schedulers: saleSchedulers,
    onCreate: onCreateScheduler,
    onUpdate: onUpdateScheduler,
    onDelete: onDeleteScheduler,
  } = useSchedulers<IScheduler>(
    params.featureId as string,
    CSchedulerEntityPaths.FEATURES,
    ESchedulerPrefix.SALE,
  );

  const {
    loading: discountLoading,
    refetch: discountOnRefresh,
    schedulers: discountSchedulers,
  } = useSchedulers<IScheduler>(
    params.featureId as string,
    CSchedulerEntityPaths.FEATURES,
    ESchedulerPrefix.DISCOUNT,
  );

  const {
    loading: trialLoading,
    refetch: trialOnRefresh,
    schedulers: trialSchedulers,
  } = useSchedulers<IScheduler>(
    params.featureId as string,
    CSchedulerEntityPaths.FEATURES,
    ESchedulerPrefix.TRIAL_DISCOUNT,
  );

  useEffect(() => {
    if (isDirty) return;

    setAssignedSchedulers((prev) => {
      const prevMap = prev[CScheduler];
      const nextDiscount = discountSchedulers || [];
      const nextSale = saleSchedulers || [];
      const nextTrial = trialSchedulers || [];

      const noChange =
        areSameScheduler(prevMap[ESchedulerPrefix.DISCOUNT], nextDiscount) &&
        areSameScheduler(prevMap[ESchedulerPrefix.SALE], nextSale) &&
        areSameScheduler(
          prevMap[ESchedulerPrefix.TRIAL_DISCOUNT] || [],
          nextTrial,
        );

      if (noChange) return prev;

      return {
        [CScheduler]: {
          ...prevMap,
          [ESchedulerPrefix.DISCOUNT]: nextDiscount,
          [ESchedulerPrefix.SALE]: nextSale,
          [ESchedulerPrefix.TRIAL_DISCOUNT]: nextTrial,
        },
      };
    });
  }, [
    trialSchedulers,
    discountSchedulers,
    saleSchedulers,
    setAssignedSchedulers,
    isDirty,
  ]);

  useEffect(() => {
    if (feature) {
      setLoading(true); // Set loading during form initialization
      formRef.setFieldsValue({
        ...feature,
        isUtilityEnabled: !!feature[utilityCounter],
      });

      // Use setTimeout to ensure setFieldsValue completes first
      setTimeout(() => {
        setDirty(false); // Reset any false dirty state
        setLoading(false); // Mark initialization complete
      }, 0);
    }
  }, [feature, formRef, setLoading, setDirty]);

  useEffect(() => {
    const cloned = (location.state as { clonedFrom?: IFeature } | null)
      ?.clonedFrom;
    if (!isEdit && cloned) {
      formRef.setFieldsValue({
        ...cloned,
        isUtilityEnabled: !!cloned[utilityCounter],
      });
      setDirty(true);
    }
  }, [isEdit, location.state, formRef, setDirty]);

  const subject = EAbilitySubjects.FEATURE;

  const disabled = loading || ability.cannot(EAbilityActions.MANAGE, subject);

  const entity = t('feature');

  const { onSuccess } = useOnFinish({ formRef, isEdit, entity });

  usePrompt();

  const handleRefresh = async () => {
    await refetch();
    formRef.setFieldsValue({
      ...feature,
      isUtilityEnabled: !!feature?.[utilityCounter],
    });
    setDirty(false);
  };

  const { DEFAULT_FORM_PROPS } = useFormProps(loading, formRef);

  const formProps = {
    ...DEFAULT_FORM_PROPS,
    async onFinish(formValues: IFeature & { isUtilityEnabled?: boolean }) {
      const cleanedValues = { ...formValues };
      if (!cleanedValues.isUtilityEnabled) {
        cleanedValues[utilityCounter] = null;
      }
      delete cleanedValues.isUtilityEnabled;

      if (isEdit) {
        modalApi.confirm({
          title: t('feature.save.confirm.title'),
          content: t('feature.save.confirm.description'),
          className: 'modalApi',
          centered: true,
          footer: (
            <Flex justify="flex-end" gap={8}>
              <Button key="cancel" onClick={() => Modal.destroyAll()}>
                {t('actions.cancel')}
              </Button>
              <Button
                key="clone"
                onClick={async () => {
                  Modal.destroyAll();
                  const newFeature = await onCreate(cleanedValues);
                  if (newFeature?.id) {
                    setDirty(false);
                    await successSaveMsg(messageApi, false, entity);
                    navigate(`${ERoutePaths.FEATURES}/${newFeature.id}`);
                    await createSchedulers({
                      entityId: newFeature.id,
                      schedulers: assignedSchedulers,
                      onCreate: onCreateScheduler,
                    });
                    await syncFeatureSubscriptions({
                      featureId: newFeature.id,
                      subscriptions: assignedSubscriptions,
                    });
                    await queryClient.invalidateQueries({
                      queryKey: [...subscriptionKeys.all, 'byFeature'],
                    });
                  }
                }}
              >
                {t('actions.clone')}
              </Button>
              <Button
                key="update"
                type="primary"
                onClick={async () => {
                  Modal.destroyAll();
                  await onUpdate(params.featureId, cleanedValues);
                  await syncSchedulers({
                    entityId: params.featureId,
                    schedulers: assignedSchedulers,
                    onCreate: onCreateScheduler,
                    onUpdate: onUpdateScheduler,
                  });
                  await syncFeatureSubscriptions({
                    featureId: params.featureId,
                    subscriptions: assignedSubscriptions,
                  });
                  await queryClient.invalidateQueries({
                    queryKey: [...subscriptionKeys.all, 'byFeature'],
                  });
                  await onSuccess<IFeature>(params.featureId);
                }}
              >
                {t('actions.update')}
              </Button>
            </Flex>
          ) as React.ReactNode,
        });
      } else {
        const feature = await onCreate(cleanedValues);
        if (feature?.id) {
          await onSuccess<IFeature>(
            `${ERoutePaths.FEATURES}/${feature.id}`,
            true,
          );
          await createSchedulers({
            entityId: feature.id,
            schedulers: assignedSchedulers,
            onCreate: onCreateScheduler,
          });
          await syncFeatureSubscriptions({
            featureId: feature.id,
            subscriptions: assignedSubscriptions,
          });
          await queryClient.invalidateQueries({
            queryKey: [...subscriptionKeys.all, 'byFeature'],
          });
        }
      }
    },
    initialValues: {
      ...DEFAULT_FEATURE(),
      isUtilityEnabled: false,
    },
  } as FormProps<IFeature>;

  const discountProps = {
    disabled,
    formRef,
    loading,
    setIsTrialed,
    currencies: Object.keys(ECurrency) as (keyof typeof ECurrency)[],
    durationTypes: Object.keys(
      EDurationTypes,
    ) as (keyof typeof EDurationTypes)[],
  };

  const tagsProps = {
    formRef,
    disabled,
    ableFor: {
      ability,
      subject: EAbilitySubjects.TAGS,
    },
    onUpdateTags: (tags: string[]) => {
      formRef.setFieldsValue({ [CNsTags]: tags });
    },
    tags: feature?.tags as string[],
    loading,
  };

  const schedulerSubject = `${EAbilitySubjects.FEATURE}.${EAbilitySubjects.SCHEDULER}`;

  const schedulerProviderProps = {
    schedulers: Object.fromEntries(
      Object.entries(assignedSchedulers[CScheduler]).map(([key, list]) => [
        key,
        (list as IScheduler[]).map(schedulerDatesToDayjs),
      ]),
    ) as any,
    onCreate: async (type: ESchedulerPrefix, scheduler: any) =>
      handleCreate(
        type,
        scheduler as IScheduler,
        setAssignedSchedulers,
        setDirty,
      ),
    onUpdate: async (type: ESchedulerPrefix, scheduler: any) =>
      handleUpdate(
        type,
        scheduler as IScheduler,
        setAssignedSchedulers,
        setDirty,
      ),
    onDelete: async (type: ESchedulerPrefix, schedulerId: string) =>
      handleDelete(
        type,
        schedulerId,
        assignedSchedulers,
        onDeleteScheduler,
        setDirty,
      ),
    loading: loading || saleLoading || discountLoading || trialLoading,
    disabled: isDisabledScheduler(
      EAbilitySubjects.FEATURE,
      disabled,
      isEdit,
      ability,
    ),
    permissions: {
      canCreate: ability.can(EAbilityActions.CREATE, schedulerSubject),
      canUpdate: ability.can(EAbilityActions.UPDATE, schedulerSubject),
      canDelete: ability.can(EAbilityActions.DELETE, schedulerSubject),
    },
  };

  const featureItems: CollapseProps['items'] = [
    {
      key: 'info',
      forceRender: true,
      label: (
        <div className={styles.chipLabel}>
          <IdcardTwoTone />
          {t('feature.info')}
        </div>
      ),
      children: (
        <FeatureInfo
          disabled={disabled}
          featureTypes={
            Object.keys(EFeatureType) as (keyof typeof EFeatureType)[]
          }
          formRef={formRef}
          isEdit={isEdit}
          slugLocked={slugLocked}
        />
      ),
      extra: <QuestionTooltip title={t('feature.info.helper')} />,
    },
    {
      key: CNsPrice,
      label: (
        <div className={styles.chipLabel}>
          <MoneyCollectTwoTone />
          {t('price.priceInfo', { type: t('feature') })}
        </div>
      ),
      forceRender: true,
      children: (
        <Row gutter={[24, 24]}>
          <Col {...flexLayout.fullColumn}>
            <FeatureDiscount
              {...discountProps}
              renderScheduler={(readOnlyFields): React.ReactNode => (
                <SchedulerProvider {...schedulerProviderProps}>
                  <SchedulersList
                    onRefresh={() => !isDirty && discountOnRefresh()}
                    readOnlyFields={readOnlyFields}
                    title={t('price.discount')}
                    type={ESchedulerPrefix.DISCOUNT}
                  />
                </SchedulerProvider>
              )}
            />
          </Col>
          {isTrialed && (
            <Col {...flexLayout.fullColumn}>
              <FeatureTrial
                {...discountProps}
                renderScheduler={(readOnlyFields): React.ReactNode => (
                  <SchedulerProvider {...schedulerProviderProps}>
                    <SchedulersList
                      onRefresh={() => !isDirty && trialOnRefresh()}
                      readOnlyFields={readOnlyFields}
                      title={t('price.trialedDiscount')}
                      type={ESchedulerPrefix.TRIAL_DISCOUNT}
                    />
                  </SchedulerProvider>
                )}
              />
            </Col>
          )}
        </Row>
      ),
      extra: <QuestionTooltip title={t('price.info.helper')} />,
    },
    {
      key: utilityCounter,
      label: (
        <div className={styles.chipLabel}>
          <ControlTwoTone />
          {t('feature.utilityInfo')}
        </div>
      ),
      forceRender: true,
      children: (
        <FeatureUtility
          disabled={disabled}
          loading={loading}
          formRef={formRef}
        />
      ),
      extra: (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'flex', alignItems: 'center', gap: 12 }}
        >
          <Form.Item
            layout="horizontal"
            name="isUtilityEnabled"
            label={t('feature.utility.enabled')}
            valuePropName="checked"
            style={{ margin: 0 }}
          >
            <Switch disabled={disabled} loading={loading} />
          </Form.Item>
          <QuestionTooltip title={t('feature.utility.helper')} />
        </div>
      ),
    },
    {
      key: 'scheduler',
      label: (
        <div className={styles.chipLabel}>
          <ScheduleTwoTone />
          {t('scheduler.header.title', {
            entity: t('price.saleAt'),
            count: assignedSchedulers[CScheduler][ESchedulerPrefix.SALE].length,
          })}
        </div>
      ),
      children: (
        <Row gutter={[24, 24]}>
          <Col {...flexLayout.fullColumn}>
            <SchedulerProvider {...schedulerProviderProps}>
              <SchedulersList
                onRefresh={() => !isDirty && saleOnRefresh()}
                title={t('price.saleAt')}
                type={ESchedulerPrefix.SALE}
              />
            </SchedulerProvider>
          </Col>
        </Row>
      ),
      extra: <QuestionTooltip title={t('scheduler.info.helper')} />,
    },
    {
      key: 'subscriptions',
      label: (
        <div className={styles.chipLabel}>
          <ControlTwoTone />
          {t('feature.assigned.subscriptions', {
            count: assignedSubscriptions.length,
          })}
        </div>
      ),
      children: (
        <Row gutter={[24, 24]}>
          <Col {...flexLayout.fullColumn}>
            <FeatureSubscriptions disabled={disabled} />
          </Col>
        </Row>
      ),
      extra: (
        <div className={styles.extraPanel}>
          <SubscriptionsDrawerButton
            disabled={disabled || !activeKeys.includes('subscriptions')}
          />
          <QuestionTooltip title={t('feature.subscriptions.helper')} />
        </div>
      ),
    },
    {
      key: CNsTags,
      forceRender: true,
      label: (
        <div className={styles.chipLabel}>
          <TagsTwoTone />
          {t('form.tags')}
        </div>
      ),
      children: (
        <Form.Item noStyle name={[CNsTags]}>
          <TagsComponent {...tagsProps} />
        </Form.Item>
      ),
      extra: <QuestionTooltip title={t('form.tag.helper')} />,
    },
    {
      ...formMainInfo({
        metadata: feature?.metadata as IMetadata,
        disabled: disabled || !isEdit,
        navigate,
      }),
    },
  ];

  return (
    <PageLayout
      title={<EditPageTitle isEdit={isEdit} name={entity} />}
      action={EAbilityActions.MANAGE}
      extra={[
        <div key="status" className={styles.extraPanel}>
          <PrivacyTag
            status={feature?.isPublic ? EPrivacyStatus.PUBLIC : 'PRIVATE'}
          />
          <StatusTag status={feature?.status ?? EStatus.PROCESSING} />
        </div>,
        <CloseButton
          key="close"
          loading={loading}
          disabled={disabled}
          onClick={() => navigate(ERoutePaths.FEATURES)}
        />,
        <SaveButton
          key="save"
          formRef={formRef}
          disabled={disabled || (!disabled && !isDirty)}
          loading={loading}
          isEdit={isEdit}
          ableFor={{ ability, subject }}
        />,
        <Toolbar<IFeature[]>
          key="toolbar"
          onRefresh={handleRefresh}
          size="small"
          entities={[feature] as IFeature[]}
          subject={subject}
          items={
            [
              {
                label: t('actions.setAs', {
                  type: feature?.isPublic
                    ? t('form.private')
                    : t('form.public'),
                }),
                icon: feature?.isPublic ? (
                  <EyeInvisibleTwoTone />
                ) : (
                  <EyeTwoTone />
                ),
                onClick: () => onPublic?.({ ...feature, id: params.featureId }),
                disabled: disabled || !isEdit || isDirty,
              },
              {
                label:
                  feature?.status === EStatus.ACTIVE
                    ? t('actions.deactivate')
                    : t('actions.activate'),
                icon:
                  feature?.status === EStatus.ACTIVE ? (
                    <PauseCircleTwoTone />
                  ) : (
                    <CheckCircleTwoTone />
                  ),
                onClick: async () => {
                  await onActivate?.({
                    ...feature,
                    id: params.featureId,
                  });
                  await refetch();
                },
                disabled: disabled || !isEdit || isDirty,
              },
              {
                label: t('actions.cloneEntity', { type: t('feature') }),
                icon: <CopyTwoTone />,
                onClick: () => {
                  const values = formRef.getFieldsValue() as IFeature;
                  navigate('/features/new', {
                    state: {
                      clonedFrom: {
                        ...values,
                        title: `${values.title}_1`,
                        slug: '',
                      },
                    },
                  });
                },
                disabled: disabled || !isEdit,
              },
              {
                label: t('actions.deleteEntity', { type: t('feature') }),
                icon: <DeleteOutlined />,
                onClick: () =>
                  deleteWarning({
                    status: feature?.status,
                    translateKey: t('feature') as string,
                    onApprove: () =>
                      onDelete?.({ ...feature, id: params.featureId }),
                    config: { modalApi },
                  }),
                danger: true,
                disabled:
                  disabled ||
                  feature?.status !== EStatus.DEACTIVATED ||
                  !isEdit,
              },
            ] as unknown as ItemType[]
          }
        />,
      ]}
      subject={subject}
    >
      <Form {...formProps}>
        <Collapse
          items={featureItems}
          activeKey={activeKeys}
          onChange={handleChange}
        />
      </Form>
    </PageLayout>
  );
};
